/**
 * OrganizationOverview (Dashboard widget)
 *
 * 4 KPI tiles pulling from GET /v1/organization/stats
 * (Schema<'OrganizationStatsResponse'>). Wave 7.2 shipped the backend
 * endpoint; Wave 3.x completes the frontend wiring.
 *
 * Dropped fields relative to the old mock:
 *   - "change" (month-over-month delta) — backend doesn't expose a
 *     prior-period comparison today. When it does, add it back via a
 *     second endpoint (e.g. ?window=30d) and render the trending arrow.
 *     For now we show the absolute count with a neutral subtitle.
 *
 * Graceful degradation: on error we show the tiles with '—' values and
 * a small retry link at the bottom. The dashboard shouldn't hard-fail
 * on one widget.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Grid, Paper, Skeleton } from '@mui/material';
import {
  Groups,
  HowToReg,
  Business,
  WorkOutline,
  LocationOn,
  PlaceOutlined,
} from '@mui/icons-material';

import { ApiError, organizationApi, type Schema } from '../../../../api';
import { useTranslation } from 'react-i18next';
import { translateApiError } from '../../../../i18n/translateApiError';

type Stats = Schema<'OrganizationStatsResponse'>;

interface TileSpec {
  label: string;
  value: number | null;
  icon: React.ReactNode;
  color: string;
  subtitle: string;
  /** P2.6: drillable widget — Paper navigates here on click. */
  path: string;
}

function buildTiles(stats: Stats | null, t: any): TileSpec[] {
  return [
    {
      label: t('dashboard:widgets.organizationOverview.totalWorkers'),
      value: stats?.total_workers ?? null,
      icon: <Groups />,
      color: '#2196F3',
      subtitle: t('dashboard:widgets.organizationOverview.allWorkers'),
      path: '/organization/workers/active',
    },
    {
      label: t('dashboard:widgets.organizationOverview.activeWorkers'),
      value: stats?.active_workers ?? null,
      icon: <HowToReg />,
      color: '#4CAF50',
      subtitle: t('dashboard:widgets.organizationOverview.currentlyActive'),
      path: '/organization/workers/active',
    },
    {
      label: t('dashboard:widgets.organizationOverview.departments'),
      value: stats?.departments ?? null,
      icon: <Business />,
      color: '#FF9800',
      // Branches AND locations: both answer "where", and locations lost its home when
      // the vacancy tile stopped captioning itself with an unrelated number.
      subtitle: t('dashboard:widgets.organizationOverview.branchesAndLocations', {
        branches: stats?.branches ?? 0,
        locations: stats?.locations ?? 0,
      }),
      path: '/organization/departments',
    },
    {
      label: t('dashboard:widgets.organizationOverview.openPositions'),
      value: stats?.open_positions ?? null,
      icon: <WorkOutline />,
      color: '#9C27B0',
      // A vacancy count needs its DENOMINATOR: "1 open position" reads very differently
      // in an org that defines 1 role versus 40. This caption used to show the LOCATION
      // count, which explained nothing about the number above it.
      subtitle: t('dashboard:widgets.organizationOverview.ofPositions', {
        count: stats?.positions ?? 0,
      }),
      path: '/organization/positions',
    },
  ];
}

const OrganizationOverview: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      const data = await organizationApi.profileApi.stats();
      setStats(data);
      setError(null);
    } catch (e) {
      setError(
        e instanceof ApiError
          ? translateApiError(e, t)
          : t('dashboard:widgets.organizationOverview.loadError'),
      );
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats, t]);

  const tiles = buildTiles(stats, t);

  return (
    <Box sx={{ height: '100%' }}>
      <Grid container spacing={1}>
        {tiles.map((metric) => (
          <Grid item xs={3} key={metric.label}>
            <Paper
              elevation={0}
              onClick={() => navigate(metric.path)}
              sx={{
                p: 1,
                height: '100%',
                borderRadius: 2,
                bgcolor: 'background.paper',
                cursor: 'pointer',
                border: '1px solid transparent',
                transition: 'border-color 120ms',
                '&:hover': { borderColor: 'divider' },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'background.paper',
                    color: metric.color,
                    mr: 1,
                  }}
                >
                  {metric.icon}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      mb: 0.5,
                      fontWeight: 600,
                      color: 'text.primary',
                    }}
                  >
                    {metric.label}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {loading && metric.value === null ? (
                      <Skeleton
                        variant="text"
                        width={36}
                        height={28}
                        sx={{ mr: 1 }}
                      />
                    ) : (
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 700,
                          mr: 1,
                          color: metric.color,
                          cursor: 'pointer',
                          '&:hover': { textDecoration: 'underline' },
                        }}
                      >
                        {metric.value ?? '—'}
                      </Typography>
                    )}
                  </Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', mt: 0.5, fontWeight: 500 }}
                  >
                    {metric.subtitle}
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {error && (
        <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <PlaceOutlined sx={{ fontSize: 14, color: 'warning.dark' }} />
          <Typography
            variant="caption"
            color="warning.dark"
            sx={{ flex: 1, fontSize: '0.7rem' }}
          >
            {error}
          </Typography>
          <Typography
            variant="caption"
            onClick={fetchStats}
            sx={{
              fontSize: '0.7rem',
              color: 'primary.main',
              fontWeight: 600,
              cursor: 'pointer',
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            {t('dashboard:widgets.organizationOverview.retry')}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default OrganizationOverview;

// LocationOn is imported to keep the existing icon bundle stable; if
// unused by a future iteration, remove the import.
void LocationOn;
